import { AppRouter } from '@/api/trpc/index.js'

import { httpBatchLink, httpLink, httpSubscriptionLink, createTRPCClient, splitLink, retryLink } from '@trpc/client'

import superjson from 'superjson'
import { useSystem } from '@/context/SystemProvider'
import { API_GATEWAY } from '@/constants/constants'
import { useSession } from '@/context/SessionProvider'

const generateJwt = async () => {
  const accountValue = useSession.getState().account
  if (!accountValue) {
    return null
  }

  const jwt = Buffer.from(
    JSON.stringify({
      sub: accountValue.injectiveAddress,
      session: accountValue.privateKey.toBech32(),
      exp: useSystem.getState().getCurrentDateTime().getTime() + 60 * 60 * 24,
    })
  )

  const sign = await useSession.getState().sign(jwt.toString('base64'))
  return {
    jwt: jwt.toString('base64'),
    signature: sign,
  }
}

const client = createTRPCClient<AppRouter>({
  links: [
    retryLink({
      retry(opts) {
        if (opts.error.data?.httpStatus === 500) {
          return opts.attempts <= 3
        }

        return false
      },
      retryDelayMs: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }),
    splitLink({
      // uses the httpSubscriptionLink for subscriptions
      condition: (op) => op.type === 'subscription',
      true: httpSubscriptionLink({
        url: `${API_GATEWAY}/trpc`,
        transformer: superjson,
        connectionParams: async () => {
          // Will be serialized as part of the URL
          const token = await generateJwt()

          if (!token) {
            return {}
          }

          return {
            token: Buffer.from(JSON.stringify(token)).toString('base64'),
          }
        },
      }),
      false: httpLink({
        url: `${API_GATEWAY}/trpc`,
        transformer: superjson,
        methodOverride: 'POST',
        headers: async (opts) => {
          if (!opts.op.path.startsWith('private.')) {
            return {}
          }
          const token = await generateJwt()

          if (!token) {
            throw new Error('No account')
          }

          return {
            authorization: 'Session: ' + Buffer.from(JSON.stringify(token)).toString('base64'),
          }
        },
      }),
    }),
  ],
})

export const useTRPCClient = () => client
